"use client";

import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentOrg } from "@/lib/org-context";
import {
  consumePendingEntry,
  mapEntryError,
  resolveAuthFailureAction,
  resolveEntryAction,
  setPendingEntry,
} from "@/lib/platform-support-entry";
import { safeNextPath } from "@/lib/safe-next-path";
import { organizationService } from "@/services/organization.service";
import { platformSupportService } from "@/services/platformSupport.service";
import {
  getPlatformSupportSession,
  setPlatformSupportSession,
  setPlatformSupportAuthFailureCallback,
  setPlatformSupportLeaveCallback,
  type PlatformSupportSessionState,
} from "@/services/api.service";

interface PlatformSupportContextValue {
  active: boolean;
  sessionId: string | null;
  orgId: string | null;
  orgName: string | null;
  expiresAt: string | null;
  enter: (sessionId: string) => Promise<void>;
  leave: () => void;
}

type State = (PlatformSupportSessionState & { orgName: string | null }) | null;

const PlatformSupportContext = createContext<PlatformSupportContextValue | null>(null);

export function PlatformSupportProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const currentUserId = user?.id ?? null;
  const { setCurrentOrganization } = useCurrentOrg();
  const [state, setState] = useState<State>(null);

  useEffect(() => {
    const stored = getPlatformSupportSession();
    if (stored) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect -- rehydrate tab-local state on mount */
      setState({ ...stored, orgName: null });
    }
  }, []);

  const leave = useCallback(() => {
    setPlatformSupportSession(null);
    setState(null);
  }, []);

  // Clears any leftover support session (storage + React state, e.g. after
  // rehydrating a stale one from sessionStorage on mount above) once auth
  // settles on "no user" — covers explicit logout, where clearTokens() only
  // clears the storage key and leaves this provider's React state (and thus
  // the banner) stale for the rest of the tab session. `leave()` never
  // touches the separate pending-entry key, and is a no-op when there's
  // nothing active, so this doesn't interfere with the pre-login stash flow.
  useEffect(() => {
    if (!authLoading && !user) leave();
  }, [authLoading, user, leave]);

  // Registers `leave` as the module-level callback apiFetch (api.service.ts)
  // invokes when it detects the support session is no longer valid
  // (SESSION_* codes, or a FORBIDDEN/ORG_LOCKED heuristic on the web-app
  // paths — see handlePlatformSupportSessionFailure) — analogous to the
  // setRefreshCallback pattern in auth-context.tsx. Without this, apiFetch
  // could only clear sessionStorage, leaving the React state (and banner)
  // stale until the next navigation.
  useEffect(() => {
    setPlatformSupportLeaveCallback(() => {
      leave();
      toast.error("Support-Sitzung beendet oder ungültig – Support-Modus wurde verlassen.");
    });
    return () => setPlatformSupportLeaveCallback(null);
  }, [leave]);

  // Registers the auth-failure callback apiFetch invokes on a *final*
  // UNAUTHORIZED (refresh already failed) while a support session may be
  // active (D5). Unlike the leave callback above, this also stashes the
  // session id so a re-login can pick support mode back up (see the
  // pending-re-entry effect below and resolveAuthFailureAction).
  useEffect(() => {
    setPlatformSupportAuthFailureCallback(() => {
      const action = resolveAuthFailureAction({ hasActiveSupportSession: !!state });
      if (!action.clearSupport) return;
      if (action.stash && state?.sessionId) {
        // Stamp with the user who was logged in when the support session
        // was active (D5), so a different user logging into this tab later
        // doesn't inherit the stash — see consumePendingEntry.
        setPendingEntry(state.sessionId, currentUserId);
      }
      leave();
      toast.error(
        "Anmeldung abgelaufen – bitte erneut anmelden, um den Support-Modus fortzusetzen."
      );
    });
    return () => setPlatformSupportAuthFailureCallback(null);
  }, [leave, state, currentUserId]);

  const enter = useCallback(
    async (sessionId: string) => {
      const session = await platformSupportService.getSession(sessionId);

      if (session.ended_at || session.revoked_at) {
        throw new Error("Diese Support-Sitzung wurde bereits beendet.");
      }
      if (new Date(session.expires_at).getTime() < Date.now()) {
        throw new Error("Diese Support-Sitzung ist abgelaufen.");
      }

      const next: PlatformSupportSessionState = {
        sessionId: session.id,
        orgId: session.org_id,
        expiresAt: session.expires_at,
      };
      // Store first so the following org fetch already carries a
      // org-matching X-Platform-Session header (see api.service.ts).
      setPlatformSupportSession(next);
      setState({ ...next, orgName: null });

      try {
        const org = await organizationService.getOrganizationById(session.org_id);
        setCurrentOrganization(org);
        setState({ ...next, orgName: org.name });
      } catch {
        // The org fetch failed after the session was already validated and
        // stored — roll back so the tab doesn't end up with an active
        // support session (and banner) but no organization behind it (D4).
        // Throw a fixed, known message rather than the original fetch
        // error: the original may be a generic/technical apiFetch error
        // that doesn't map to a specific mapEntryError() case, which would
        // otherwise surface as the wrong toast (and, since both the caller
        // in PlatformSupportEntryWatcher and this rollback path can each
        // show a toast, risk a confusing double toast on top of that).
        leave();
        throw new Error("Organisation der Support-Sitzung konnte nicht geladen werden.");
      }
    },
    [setCurrentOrganization, leave]
  );

  // Post-login re-entry (D1): retries a support session id that got stashed
  // by the entry watcher because the user wasn't logged in yet when
  // `?platform_session=<id>` first arrived. Runs whenever the auth state
  // settles on a logged-in user; consumePendingEntry() is a no-op if there's
  // nothing pending, and always clears the pending key itself either way.
  useEffect(() => {
    if (authLoading || !user) return;
    void consumePendingEntry(enter, currentUserId).catch((e) => {
      console.error("[platform-support] Pending-Einstieg fehlgeschlagen:", e);
      toast.error(mapEntryError(e));
    });
  }, [authLoading, user, enter, currentUserId]);

  const value: PlatformSupportContextValue = {
    active: !!state,
    sessionId: state?.sessionId ?? null,
    orgId: state?.orgId ?? null,
    orgName: state?.orgName ?? null,
    expiresAt: state?.expiresAt ?? null,
    enter,
    leave,
  };

  return (
    <PlatformSupportContext.Provider value={value}>
      <Suspense fallback={null}>
        <PlatformSupportEntryWatcher enter={enter} />
      </Suspense>
      {children}
    </PlatformSupportContext.Provider>
  );
}

/**
 * Detects `?platform_session=<id>` on any page and reacts based on auth
 * state (resolveEntryAction): waits while auth is still loading, enters
 * support mode right away if already logged in, or stashes the id for
 * post-login retry (see the pending-re-entry effect in the provider above)
 * if not. The param is only stripped once a decision (enter/stash) has
 * actually been made — not while still waiting on auth. Isolated in its own
 * Suspense boundary because useSearchParams() requires one in the App Router.
 */
function PlatformSupportEntryWatcher({
  enter,
}: {
  enter: (sessionId: string) => Promise<void>;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    const sessionId = searchParams.get("platform_session");
    if (!sessionId) return;
    const action = resolveEntryAction({ authLoading, user, sessionId });
    if (action === "wait") return;

    if (action === "stash") {
      // No user is known yet at this point (that's exactly why we're
      // stashing) — stamp with `userId: null` so consumePendingEntry
      // accepts it after whichever user logs in next.
      setPendingEntry(sessionId, null);
      toast.success("Bitte anmelden, um die Support-Sitzung zu öffnen.");
    } else {
      enter(sessionId).catch((e) => {
        console.error("[platform-support] Einstieg in Support-Modus fehlgeschlagen:", e);
        toast.error(mapEntryError(e));
      });
    }

    const params = new URLSearchParams(searchParams.toString());
    params.delete("platform_session");
    const query = params.toString();
    router.replace(safeNextPath(query ? `${pathname}?${query}` : pathname) ?? "/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per incoming param/auth-state change, not on every enter identity change
  }, [searchParams, authLoading, user]);

  return null;
}

export function usePlatformSupport(): PlatformSupportContextValue {
  const ctx = useContext(PlatformSupportContext);
  if (!ctx) {
    throw new Error("usePlatformSupport must be used within PlatformSupportProvider");
  }
  return ctx;
}
